import {
  Adressvorschlag,
  App,
  FEVorvertragsnummer,
  UploadedFile,
  Wartezeitverzicht,
} from '@ps20-frontend/frontend-models';
import { ProductUw } from '@ps20-frontend/uw';
import {
  EinbruchmeldeanlageVdsKlasse,
  ExtremwetterschutzSelbstbeteiligung,
  GegenstandCategory,
  HausArt,
  Produktlinienname,
  PrometheusCounterIncreasePayload,
  Stockwerk,
  TaasNachlassVorteil,
  Tierart,
  TresorSicherheitsstufe,
} from '@ps20/bff-models';
import { AngebotsId, UnfCheckoutProduktPayload } from '@ps20-frontend/shop-integration-redux';
import { UserInputCompletionStatus } from '@ps20-frontend/gui-components';

export enum SG_ADRESSE {
  STRASSE = 'STRASSE',
  POSTFACH = 'POSTFACH',
  GROSSKUNDE = 'GROSSKUNDE',
}

export enum TresorVerbauung {
  FREISTEHEND = 'FREISTEHEND',
  EINGEMAUERT = 'EINGEMAUERT',
}

export enum SicherungVorhanden {
  JA = 'JA',
  NEIN = 'NEIN',
  WIRD_NACHGERUESTET = 'WIRD_NACHGERUESTET',
}

export enum SicherungVorhandenMitRelevanz {
  JA = 'JA',
  NEIN = 'NEIN',
  WIRD_NACHGERUESTET = 'WIRD_NACHGERUESTET',
  NICHT_RELEVANT = 'NICHT_RELEVANT',
}

export class Produkt implements AngebotsId {
  appId: App;
  angebotsId?: string;
  angebotsdatum?: string;
  produktangebotId: string; // Notwendig um einen eindeutigen Hash zu erzeugen?
  name: string;
  produktlinie?: Produktlinienname;
  beginn: string;
  ablauf: string;
  laufzeit: number;
  versichertesRisiko: string;
  sofortschutzAvailable: boolean;
  sofortschutzActive: boolean;
  beginndatumChangeable: boolean;
  sofortschutzAblauf: string;
  vorvertragsnummer?: FEVorvertragsnummer[];
  abweichungVomZielmarkt: boolean;
  zielmarktBegruendung: string;
  vorvertragSofortschutz?: boolean;
  beibehalten: boolean;
  wirksamkeitsbeginn?: string;
  shouldDisableBeginndatum?: boolean;
  countersIncreases: Array<PrometheusCounterIncreasePayload>;
  extremwetterschutzBeratunghinweis?: boolean;
  premiumAndExtremwetterDisabled?: boolean;
  isImmobilieImAusland?: boolean;
  beitragsfreiBeibehalten?: boolean;
  vorvertragSofortschutzAblaufDatum?: string;
  vorteilsrabattBelastungInput?: VorteilsrabattBelastungInput;
  kooperation?: string;
  showEleUpload?: boolean;
  zuschlagRisiko?: number;
}

export interface VorteilsrabattBelastungInput {
  taasNachlassVorteil?: TaasNachlassVorteil;
  cpSternSum: number;
  jahresNettoBausteineSum: number;
}

//TODO: Wenn Funktionen angepasst werden, könnte man direkt Wartezeitverzicht extenden ohne einen Wrapper zu benötigen
export interface WartezeitverzichtWrapperInterface {
  wartezeitverzicht: Wartezeitverzicht;
}

export interface VerschlusspflichtigeWertsachen {
  bargeld?: number;
  schmucksachen?: number;
  schmucksachenEinzelwert?: number;
  armbandTaschenuhren?: number;
  armbandTaschenuhrenEinzelwert?: number;
  wertpapiere?: number;
  edelmetalle?: number;
  summe?: number;
  tresoreVorhanden?: boolean;
  modified: boolean;
}

export interface NichtVerschlusspflichtigeWertsachen {
  kunstgegenstaende?: number;
  kunstgegenstaendeEinzelwert?: number;
  silbersachen?: number;
  antiquitaten?: number;
  summe?: number;
  einbruchmeldeanlageVorhanden?: boolean;
  modified: boolean;
}

export interface Modeaccessoires {
  taschen?: number;
  schuhpaare?: number;
  guertel?: number;
  sonstige?: number;
  summe?: number;
  modified: boolean;
}

export interface Wertsachen {
  verschlusspflichtige?: VerschlusspflichtigeWertsachen;
  nichtVerschlusspflichtige?: NichtVerschlusspflichtigeWertsachen;
  modeaccessoires?: Modeaccessoires;
  summe?: number;
}

export interface VhvCheckoutProdukt extends Produkt {
  uw?: ProductUw;
  showAngabenZumHaushalt: boolean;
  hasWertsachen: boolean;
  wertsachenValue?: number;
  versicherungssumme?: number;
  showWertsachensummeQuestion?: boolean;
  wertsachen?: Wertsachen;
  staendigBewohnt?: boolean;
  stockwerk?: Stockwerk;
  hausart?: HausArt;
  einbruchMeldeanlage?: EinbruchMeldeanlage;
  tresor?: Tresor;
  mechanischeSicherungen?: MechanischeSicherungen;
  filesToDelete: { uploadId: string; componentId: string }[];
  isTragerisikoSelected?: boolean;
  extremwetterschutzSelbstbeteiligung?: ExtremwetterschutzSelbstbeteiligung;
  zuersZone?: string;
  entschaedigungsbetragRad?: number;
  tragerisiko?: { entschaedigungsgrenze: number | null; einzelwert: number | null };
}

export type VhvV2CheckoutProdukt = VhvCheckoutProdukt;

export interface VwgCheckoutProdukt extends Produkt {
  uw?: ProductUw;
  filesToDelete: { uploadId: string; componentId: string }[];
  showEleUpload?: boolean;
  extremwetterschutzSelbstbeteiligung?: ExtremwetterschutzSelbstbeteiligung;
  zuersZone?: number;
}

export interface RsBaseProdukt extends Produkt, WartezeitverzichtWrapperInterface {}

export interface RsCheckoutProdukt extends RsBaseProdukt {
  hasEinFahrzeugVersichert: boolean;
  amtlichesKennzeichen: string;
}

export type RsVermietungCheckoutProdukt = RsBaseProdukt;

export interface UnfCheckoutProdukt extends Produkt {
  unfallVp?: UnfCheckoutProduktPayload['unfallVp'];
}

export interface GegeCheckoutProdukt extends Produkt {
  gegenstaende?: {
    id: string;
    kategorie: {
      id: GegenstandCategory;
      name: string;
    };
    kaufdatum: string;
    versicherungssumme: number;
    modell?: string;
    informationen?: string;
    beitrag: number;
  }[];
  fahrradVorschaeden?: {
    provided: boolean;
    count: number;
  };
}

export interface TkvCheckoutProdukt extends Produkt {
  tierart: Tierart;
  inBehandlung: boolean;
  geburtsdatumTier: string;
}

export interface TkvV2CheckoutProdukt extends Produkt {
  animalType: Tierart;
  animalBirthdate: string;
  hasIllnesses: boolean;
}

export interface Sicherungsglaeubiger {
  angebotsId: string;
  index: number;
  name: string;
  plz: string;
  ort: string;
  strasse: string;
  hausnr: string;
  aktenzeichen: string;
  postfach: string;
  adressArt: SG_ADRESSE;
  adressvorschlag: Adressvorschlag;
}

export interface EinbruchMeldeanlage {
  status: UserInputCompletionStatus;
  saved: boolean;
  klasse: EinbruchmeldeanlageVdsKlasse;
  vds2170: boolean;
  vds2529: boolean;
  meldeanlageBeschreibung: string;
  files: UploadedFile[];
}

export interface Tresor {
  status: UserInputCompletionStatus;
  saved: boolean;
  eintraege: TresorEintrag[];
}

export interface TresorEintrag {
  sicherheitsstufe: TresorSicherheitsstufe;
  verbauung: TresorVerbauung;
  gewicht: number;
  ueberwachung: boolean;
  verankert?: boolean;
  files: UploadedFile[];
  tresorId: string;
}

export interface MechanischeSicherungen {
  status: UserInputCompletionStatus;
  saved: boolean;
  aussentueren: {
    schliesszylinder: {
      vorhanden: SicherungVorhanden;
      nachruestungDatum: string;
    };
    querriegelschloss: {
      vorhanden: SicherungVorhanden;
      nachruestungDatum: string;
    };
    verglasungen: {
      vorhanden: boolean;
      gitter: {
        vorhanden: SicherungVorhanden;
        nachruestungDatum: string;
      };
      eh1Verglasung: {
        vorhanden: SicherungVorhanden;
        nachruestungDatum: string;
      };
      andereSicherung: {
        vorhanden: SicherungVorhanden;
        nachruestungDatum: string;
        nachruestungBeschreibung: string;
      };
    };
  };
  balkontueren: {
    zusatzschloss: {
      vorhanden: SicherungVorhandenMitRelevanz;
      nachruestungDatum: string;
    };
    zweifachverriegelung: {
      vorhanden: SicherungVorhandenMitRelevanz;
      nachruestungDatum: string;
    };
    andereSicherung: {
      vorhanden: SicherungVorhandenMitRelevanz;
      nachruestungDatum: string;
      nachruestungBeschreibung: string;
    };
  };
  fenster: {
    zusatzschloss: {
      vorhanden: SicherungVorhandenMitRelevanz;
      nachruestungDatum: string;
    };
    zweifachverriegelung: {
      vorhanden: SicherungVorhandenMitRelevanz;
      nachruestungDatum: string;
    };
    gitter: {
      vorhanden: SicherungVorhandenMitRelevanz;
      nachruestungDatum: string;
    };
    andereSicherung: {
      vorhanden: SicherungVorhandenMitRelevanz;
      nachruestungDatum: string;
      nachruestungBeschreibung: string;
    };
  };
  kellerfenster: {
    stahllochblende: {
      vorhanden: SicherungVorhandenMitRelevanz;
      nachruestungDatum: string;
    };
    gitter: {
      vorhanden: SicherungVorhandenMitRelevanz;
      nachruestungDatum: string;
    };
    lichtschachtrost: {
      vorhanden: SicherungVorhandenMitRelevanz;
      nachruestungDatum: string;
    };
  };
  weitereAbsicherungen: string;
  files: UploadedFile[];
}

export interface EleUploadedFile {
  id: string;
  status: FileStatus;
  angebotsId?: string;
}

export enum FileStatus {
  UPLOADED,
  DELETED,
}
